import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, ChevronRight, SlidersHorizontal, X, Share2, Twitter, Linkedin, Facebook, Link as LinkIcon, Check } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import BlogSidebar from './BlogSidebar';
import TrendingBlogs from './TrendingBlogs';
import BlogGrid from './BlogGrid';
import ExperienceBanner from './ExperienceBanner';
import BlogNavbar from './BlogNavbar';
import { fetchBlogs, fetchBlogsMeta } from '../services/blogService';
import CustomSelect from './ui/CustomSelect';
import BlogCardSkeleton from './skeletons/BlogCardSkeleton';
import { applySeo } from '../utils/seo';

const BLOGS_SHARE_URL = window.location.origin + '/blogs';
const BLOGS_SHARE_TEXT = 'Explore student blogs, college guides & career tips on Race Next Step!';

const ShareButton = () => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const ref = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    const handleOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const handleToggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const dropdownWidth = 192;
      const left = rect.right - dropdownWidth < 0 ? rect.left : rect.right - dropdownWidth;
      setDropdownPos({ top: rect.bottom + 6, left });
    }
    setOpen((o) => !o);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(BLOGS_SHARE_URL).then(() => {
      setCopied(true);
      setTimeout(() => { setCopied(false); setOpen(false); }, 2000);
    });
  };

  const shareOptions = [
    { key: 'twitter', label: 'Twitter / X', Icon: Twitter, href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(BLOGS_SHARE_URL)}&text=${encodeURIComponent(BLOGS_SHARE_TEXT)}` },
    { key: 'linkedin', label: 'LinkedIn', Icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(BLOGS_SHARE_URL)}` },
    { key: 'facebook', label: 'Facebook', Icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(BLOGS_SHARE_URL)}` }
  ];

  return (
    <div ref={ref}>
      <button
        ref={btnRef}
        type="button"
        onClick={handleToggle}
        className={`flex h-9 w-9 items-center justify-center rounded-xl border shadow-sm transition-colors ${
          open ? 'border-[#d7b3f4] bg-[#f4ecff] text-[#7f3df1]' : 'border-gray-200 bg-white text-[#9a51a0] hover:bg-[#f4ecff]'
        }`}
      >
        <Share2 className="h-4 w-4" />
      </button>

      {open && (
        <div
          style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}
          className="w-48 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-[0_16px_40px_rgba(15,23,42,0.18)]"
        >
          <div className="bg-gradient-to-r from-[#9a51a0] to-[#f36f21] px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-white">
            Share Blogs Page
          </div>
          <div className="space-y-1 p-2">
            {shareOptions.map(({ key, label, Icon, href }) => (
              <a key={key} href={href} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}
                className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-gray-50"
              >
                <Icon className="h-4 w-4 shrink-0 text-[#9a51a0]" />{label}
              </a>
            ))}
            {'share' in navigator && (
              <button type="button" onClick={() => { navigator.share({ title: 'Race Next Step Blogs', text: BLOGS_SHARE_TEXT, url: BLOGS_SHARE_URL }); setOpen(false); }}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-gray-50"
              >
                <Share2 className="h-4 w-4 shrink-0 text-[#9a51a0]" />More Options
              </button>
            )}
            <button type="button" onClick={handleCopy}
              className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${
                copied ? 'bg-green-50 text-green-600' : 'text-slate-700 hover:bg-gray-50'
              }`}
            >
              {copied ? <Check className="h-4 w-4 shrink-0" /> : <LinkIcon className="h-4 w-4 shrink-0 text-[#9a51a0]" />}
              {copied ? 'Copied!' : 'Copy Link'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const SORT_OPTIONS = [
  { value: 'latest', label: 'Latest' },
  { value: 'trending', label: 'Trending' },
  { value: 'most_viewed', label: 'Most Viewed' },
  { value: 'most_liked', label: 'Most Liked' },
  { value: 'most_commented', label: 'Most Commented' }
];

const BlogsPage = () => {
  const [searchParams] = useSearchParams();
  const articlesSectionRef = useRef(null);
  const [meta, setMeta] = useState({
    totals: { allBlogs: 0 },
    categories: [],
    trendingBlogs: [],
    trendingTags: []
  });
  const [blogs, setBlogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 6, total: 0, totalPages: 1 });
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTag, setSelectedTag] = useState('');
  const [sortBy, setSortBy] = useState(() => {
    const sortParam = searchParams.get('sort');
    return SORT_OPTIONS.some((o) => o.value === sortParam) ? sortParam : 'latest';
  });
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const scrollToArticlesSection = () => {
    articlesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    const sortParam = searchParams.get('sort');
    if (sortParam && SORT_OPTIONS.some((o) => o.value === sortParam)) {
      setSortBy(sortParam);
      setPagination((current) => ({ ...current, page: 1 }));
      scrollToArticlesSection();
    }
  }, [searchParams]);

  useEffect(() => {
    applySeo({
      title: 'Student Blogs and College Guides | Race Next Step',
      description: 'Read student blogs, college guides, career tips, and education insights on Race Next Step.',
      canonicalUrl: '/blogs',
      ogType: 'website'
    });
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadMeta = async () => {
      try {
        const data = await fetchBlogsMeta({ trendingBlogLimit: 3, trendingTagLimit: 8 });
        if (isMounted) {
          setMeta({
            totals: { allBlogs: data?.totals?.allBlogs || 0 },
            categories: Array.isArray(data?.categories) ? data.categories : [],
            trendingBlogs: Array.isArray(data?.trendingBlogs) ? data.trendingBlogs : [],
            trendingTags: Array.isArray(data?.trendingTags) ? data.trendingTags : []
          });
        }
      } catch (fetchError) {
        if (isMounted) setError(fetchError.message || 'Failed to load blog meta');
      }
    };
    loadMeta();
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    const loadBlogs = async () => {
      try {
        const data = await fetchBlogs({
          page: pagination.page,
          limit: pagination.limit,
          search: searchQuery,
          category: selectedCategory !== 'all' ? selectedCategory : '',
          tag: selectedTag,
          sort: sortBy
        });
        if (isMounted) {
          setBlogs(data.items);
          setPagination((current) => ({
            page: data?.pagination?.page || current.page || 1,
            limit: data?.pagination?.limit || current.limit || 6,
            total: data?.pagination?.total || 0,
            totalPages: data?.pagination?.totalPages || 1
          }));
          setError('');
        }
      } catch (fetchError) {
        if (isMounted) {
          setBlogs([]);
          setError(fetchError.message || 'Failed to load blogs');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadBlogs();
    return () => { isMounted = false; };
  }, [pagination.page, pagination.limit, searchQuery, selectedCategory, selectedTag, sortBy]);

  const visiblePagination = useMemo(() => {
    return Array.from({ length: pagination.totalPages || 1 }, (_, index) => index + 1);
  }, [pagination.totalPages]);

  const handleSearchSubmit = () => {
    setPagination((current) => ({ ...current, page: 1 }));
    setSearchQuery(searchInput.trim());
    scrollToArticlesSection();
  };

  const handleCategorySelect = (categoryKey) => {
    setSelectedCategory(categoryKey);
    setPagination((current) => ({ ...current, page: 1 }));
    setShowMobileSidebar(false);
  };

  const handleTagSelect = (tag) => {
    const normalizedTag = String(tag || '').trim().toLowerCase();
    setSelectedTag((current) => (current.toLowerCase() === normalizedTag ? '' : normalizedTag));
    setPagination((current) => ({ ...current, page: 1 }));
    setShowMobileSidebar(false);
  };

  const totalBlogCount = meta?.totals?.allBlogs || 0;
  const trendingBlogCount = Array.isArray(meta?.trendingBlogs) ? meta.trendingBlogs.length : 0;
  const heroReadTime = blogs.length
    ? `${Math.max(...blogs.map((blog) => Number.parseInt(blog.readTime, 10) || 1))} min`
    : '1 min';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 text-[#1f1730]">
      <BlogNavbar
        title="Blogs Hub"
        subtitle="Explore articles, student stories, and expert college guidance"
      />

      {/* Overview Section */}
      <section className="mx-auto max-w-7xl px-3 pb-4 sm:px-4 lg:px-8">
        <div className="overflow-hidden rounded-2xl border border-white/80 bg-white shadow-sm sm:rounded-[28px]">
          <div className="bg-[radial-gradient(circle_at_top_left,_rgba(154,81,160,0.14),_transparent_34%),radial-gradient(circle_at_top_right,_rgba(243,111,33,0.12),_transparent_28%)] px-4 py-4 sm:px-6 sm:py-5 lg:px-8 lg:py-6">
            <div className="grid gap-4 lg:grid-cols-[1.4fr_0.6fr] lg:items-center">

              {/* Left - Search */}
              <div>
                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.28em] text-[#9a51a0] sm:text-xs">
                  Blogs Overview
                </p>
                <h2 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl lg:text-3xl">
                  Explore, Learn &{' '}
                  <span className="bg-gradient-to-r from-purple-600 to-orange-500 bg-clip-text text-transparent">
                    Grow
                  </span>
                </h2>
                <p className="mt-1.5 text-xs leading-6 text-slate-600 sm:text-sm">
                  Real experiences, expert guides, and honest student stories to help you make better decisions.
                </p>

                <div className="mt-3 flex items-center gap-2">
                  <div className="flex flex-1 gap-2 rounded-2xl border border-gray-100 bg-white p-2 shadow-sm">
                    <div className="flex flex-1 items-center gap-2 rounded-xl bg-gray-50 px-3 py-2">
                      <Search className="h-4 w-4 shrink-0 text-[#8c4edc]" />
                      <input
                        type="text"
                        placeholder="Search blogs..."
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                        onKeyDown={(event) => { if (event.key === 'Enter') handleSearchSubmit(); }}
                        className="w-full bg-transparent text-sm text-[#251b38] outline-none placeholder:text-[#8d81a1]"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleSearchSubmit}
                      className="rounded-xl bg-gradient-to-r from-[#7f3df1] to-[#ff7f32] px-4 py-2 text-xs font-semibold text-white shadow-sm transition-transform hover:scale-[1.01] sm:px-6 sm:text-sm"
                    >
                      Search
                    </button>
                  </div>
                  <div className="lg:hidden">
                    <ShareButton />
                  </div>
                </div>
              </div>

              {/* Right - Stats */}
              <div className="grid grid-cols-3 gap-2 lg:grid-cols-1">
                <div className="rounded-2xl border border-purple-100 bg-gradient-to-br from-purple-50 to-white p-3 shadow-sm">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#9a51a0]">Articles</p>
                  <p className="mt-1 text-xl font-bold text-slate-900 sm:text-2xl">{totalBlogCount}</p>
                  <p className="mt-0.5 hidden text-xs text-slate-600 sm:block">Fresh guides and stories.</p>
                </div>
                <div className="rounded-2xl border border-orange-100 bg-gradient-to-br from-orange-50 to-white p-3 shadow-sm">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#f36f21]">Trending</p>
                  <p className="mt-1 text-xl font-bold text-slate-900 sm:text-2xl">{String(trendingBlogCount).padStart(2, '0')}</p>
                  <p className="mt-0.5 hidden text-xs text-slate-600 sm:block">Popular reads right now.</p>
                </div>
                <div className="rounded-2xl border border-slate-100 bg-gradient-to-br from-slate-50 to-white p-3 shadow-sm">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Read Time</p>
                  <p className="mt-1 text-xl font-bold text-slate-900 sm:text-2xl">{heroReadTime}</p>
                  <p className="mt-0.5 hidden text-xs text-slate-600 sm:block">Quick practical reads.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Articles Section */}
      <section
        id="articles-section"
        ref={articlesSectionRef}
        className="px-3 py-4 sm:px-4 sm:py-6 lg:px-8 lg:py-8"
      >
        <div className="mx-auto max-w-7xl">

          {/* Header Row */}
          <div className="mb-4 flex items-center justify-between gap-3 sm:mb-6">
            <h2 className="text-xl font-extrabold text-[#20192d] sm:text-2xl lg:text-3xl">
              Latest <span className="text-[#a03de0]">Articles</span>
            </h2>
            <div className="flex items-center gap-2">
              {/* Mobile Filter Button */}
              <button
                type="button"
                onClick={() => setShowMobileSidebar(true)}
                className="flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-[#534366] shadow-sm xl:hidden"
              >
                <SlidersHorizontal className="h-3.5 w-3.5" />
                Filters
                {(selectedCategory !== 'all' || selectedTag) && (
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[#9a51a0] text-[10px] text-white">
                    {(selectedCategory !== 'all' ? 1 : 0) + (selectedTag ? 1 : 0)}
                  </span>
                )}
              </button>

              {/* Sort */}
              <CustomSelect
                id="blog-sort"
                name="sortBy"
                value={sortBy}
                options={SORT_OPTIONS}
                onChange={(event) => {
                  setSortBy(event.target.value);
                  setPagination((current) => ({ ...current, page: 1 }));
                }}
                buttonClassName="px-3 py-2 rounded-xl border-gray-200 text-xs min-w-[120px] sm:min-w-[150px]"
              />
              {/* Share button - desktop only in header */}
              <div className="hidden lg:block">
                <ShareButton />
              </div>
            </div>
          </div>

          {/* Main Grid */}
          <div className="grid gap-6 xl:grid-cols-[260px_minmax(0,1fr)]">

            {/* Sidebar - Desktop */}
            <div className="hidden xl:block">
              <BlogSidebar
                categories={meta.categories}
                trendingTags={meta.trendingTags}
                selectedCategory={selectedCategory}
                selectedTag={selectedTag}
                onCategorySelect={handleCategorySelect}
                onTagSelect={handleTagSelect}
                editorPick={meta.trendingBlogs[0] || null}
              />
            </div>

            {/* Content */}
            <div className="min-w-0">
              <TrendingBlogs blogs={meta.trendingBlogs} />

              {error ? (
                <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">
                  {error}
                </div>
              ) : null}

              {loading ? (
                <div className="mt-6 rounded-[30px] border border-gray-100 bg-white p-5 shadow-sm sm:p-6">
                  <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                    {[1, 2, 3, 4, 5, 6].map((n) => <BlogCardSkeleton key={n} />)}
                  </div>
                </div>
              ) : (
                <BlogGrid blogs={blogs} />
              )}

              {/* Pagination */}
              <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
                {visiblePagination.map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setPagination((current) => ({ ...current, page: item }))}
                    className={`flex h-9 min-w-[36px] items-center justify-center rounded-xl px-3 text-sm font-semibold transition sm:h-11 sm:min-w-[44px] sm:px-4 ${
                      item === pagination.page
                        ? 'bg-gradient-to-r from-[#8a43e6] to-[#d456b4] text-white shadow-md'
                        : 'border border-gray-100 bg-white text-[#5e4d72] shadow-sm hover:bg-gray-50'
                    }`}
                  >
                    {item}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() =>
                    setPagination((current) => ({
                      ...current,
                      page: Math.min(current.page + 1, current.totalPages || 1)
                    }))
                  }
                  className="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-100 bg-white text-[#a03de0] shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:h-11 sm:w-11"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Sidebar Drawer */}
      {showMobileSidebar && (
        <div className="fixed inset-0 z-50 xl:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowMobileSidebar(false)}
          />
          {/* Drawer */}
          <div className="absolute bottom-0 left-0 right-0 max-h-[85vh] overflow-y-auto rounded-t-3xl bg-gray-50 p-4 shadow-2xl sm:left-auto sm:right-0 sm:top-0 sm:bottom-0 sm:max-h-full sm:w-80 sm:rounded-none sm:rounded-l-3xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-[#20192d]">Filters</h3>
              <button
                type="button"
                onClick={() => setShowMobileSidebar(false)}
                className="flex h-8 w-8 items-center justify-center rounded-xl bg-gray-200 text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <BlogSidebar
              categories={meta.categories}
              trendingTags={meta.trendingTags}
              selectedCategory={selectedCategory}
              selectedTag={selectedTag}
              onCategorySelect={handleCategorySelect}
              onTagSelect={handleTagSelect}
              editorPick={meta.trendingBlogs[0] || null}
            />
          </div>
        </div>
      )}

      <ExperienceBanner />
    </div>
  );
};

export default BlogsPage;
